import type React from 'react';
import { type ComponentProps, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, type To, useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import VerificationLayout from '@/features/verification/layout';
import { useProfilePrivacy } from '@/hooks/queries/use-profile-privacy';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { useUserAttributes } from '@/hooks/queries/use-user-attributes';
import type { Never } from '@/types/generics';
import { isInstitutionalUserSegment } from '@/utils/user-segment';
import VerificationPrivacyNotice from './privacy-notice';
import VerificationSumsub from './sumsub';

type VerificationBaseProps = React.PropsWithChildren<{
  description?: React.ReactNode;
  layoutProps?: ComponentProps<typeof VerificationLayout>;
  /*
   * Note: This doesn't control which verification level is displayed (that's determined by the access token).
   * Instead, it's used to check if the user is already verified for a level matching the identifier (and if so, redirect to the next page).
   *
   * If no level identifier is provided, the user will be redirected to the next page if they are already verified regardless of level.
   */
  levelIdentifier?: string | string[] | readonly string[];
  prev?: To;
  sdkConfig?: ComponentProps<typeof VerificationSumsub>['sdkConfig'];
  showPrevButton?: boolean;
}>;

type VerificationRedirectProps = {
  next: To;
};

type VerificationCallbackProps = {
  onChange: ComponentProps<typeof VerificationSumsub>['onChange'];
};

export type VerificationProps = VerificationBaseProps &
  ((VerificationRedirectProps & Never<VerificationCallbackProps>) | (VerificationCallbackProps & Never<VerificationRedirectProps>));

const VerificationContent = ({ children, description, levelIdentifier, next, onChange, prev, sdkConfig, showPrevButton = true }: VerificationProps) => {
  const { isLoading: isLoadingPrivacy, data: privacy, invalidate: invalidatePrivacy } = useProfilePrivacy();
  const { isLoading: isLoadingVerification, data: verificationInformation, set: setVerificationInformation } = useProfileVerificationInformation();
  const { isLoading: isLoadingUserAttributes, data: userAttributes, set: setUserAttributes } = useUserAttributes();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isRedirecting = useRef(false);
  const isLoading = isLoadingPrivacy || isLoadingVerification || isLoadingUserAttributes;
  const { level: verificationLevelName, status: verificationStatus } = verificationInformation?.verification ?? {};
  const { segment: userSegment } = userAttributes ?? {};
  const isInstitutional = isInstitutionalUserSegment(userSegment);

  const isVerificationLevelIdentifierMatch = useCallback(
    (levelName: string) => {
      return levelIdentifier
        ? typeof levelIdentifier === 'string'
          ? levelName.includes(levelIdentifier)
          : levelIdentifier.some((identifier) => levelName.includes(identifier))
        : true;
    },
    [levelIdentifier],
  );

  const handleChange: ComponentProps<typeof VerificationSumsub>['onChange'] = onChange
    ? onChange
    : async (data) => {
        /**
         * TODO: We're receiving onApplicantStatusChanged events for levels that are no longer included in the active workflow.
         * This is causing issues where we require the level to match a specific identifier, as we're redirecting the user without their
         * verification being completed on a matching level (which (in certain cases) is a requirement on the subsequent page(s),
         * ultimately resulting in an infinite redirect loop).
         *
         * Given the access token is generated for the entry point of the active workflow, and Sumsub has confirmed "generating an access token"
         * for a level essentially restarts verification, it's not clear why this is happening. This is a temporary change to see what the
         * behaviour of the SDK is if we don't tear it down/skip the redirect when the level doesn't match.
         */
        if (levelIdentifier && !isVerificationLevelIdentifierMatch(data.levelName)) {
          console.warn(`Verification level ${levelIdentifier} not matched, current level: ${data.levelName}`);
          return;
        }

        if (isRedirecting.current) return;

        isRedirecting.current = true;

        /*
         * We can't invalidate and/or refetch verificationInformation here as it's updated asynchronously via a webhook, meaning
         * the updated verification status won't be immediately available. We could poll for a change in status, but since we
         * already know the user's application has been submitted, we can safely infer the status is at least VERIFICATION_STARTED.
         * So we set the status/verificationInformation manually to allow the user to proceed. Note we also set userAttributes (which
         * includes its own verified status) for completeness.
         */
        setVerificationInformation({
          verification: {
            level: data.levelName,
            status: 'VERIFICATION_STARTED',
            verification_date: data.creationDate,
          },
        });
        setUserAttributes({ verified: 'started' });

        if (next) navigate(next, { replace: true });
      };

  if (isLoading || isRedirecting.current) return <LoadingCard />;

  if (next && verificationStatus === 'VERIFICATION_SUCCEEDED' && (!levelIdentifier || isVerificationLevelIdentifierMatch(verificationLevelName ?? ''))) {
    return <Navigate to={next} replace />;
  }

  return (
    <div className="relative">
      <PageHeading>
        <PageHeadingTitle>{t(isInstitutional ? 'verification.institution.title' : 'verification.title')}</PageHeadingTitle>
        {description ? description : <PageHeadingDescription>{t(isInstitutional ? 'verification.institution.description' : 'verification.description')}</PageHeadingDescription>}
      </PageHeading>
      {prev && showPrevButton ? <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={() => navigate(prev)} /> : null}
      {privacy?.date ? (
        <VerificationSumsub sdkConfig={sdkConfig} onChange={handleChange}>
          {children}
        </VerificationSumsub>
      ) : (
        <VerificationPrivacyNotice onAccepted={invalidatePrivacy} />
      )}
    </div>
  );
};
VerificationContent.displayName = 'VerificationContent';

const Verification = ({ layoutProps, ...props }: VerificationProps) => {
  const { isLoading: isLoadingPrivacy, data: privacy } = useProfilePrivacy();
  const { isLoading: isLoadingVerification } = useProfileVerificationInformation();
  const isLoading = isLoadingPrivacy || isLoadingVerification;

  return (
    <VerificationLayout fill={!isLoading && !privacy?.date} {...layoutProps}>
      <VerificationContent {...props} />
    </VerificationLayout>
  );
};
Verification.displayName = 'Verification';

export default Verification;
